import type { Tables, TablesInsert } from "@shared/database.types.ts";
import useSWR, { type SWRConfiguration } from "swr";
import { supabaseClient } from "@/lib/supabase";

export type SubjectSupa = Tables<"subjects">;
export type StaffSupa = Tables<"staff">;
export type StaffPublic = Tables<"staff_public">;
export type InquirySupaInsert = TablesInsert<"inquiries">;
export type ClassSummaryRaw = Tables<"class_summary">;

const EventSchema = z.object({
  event_id: z.number(),
  title: z.string(),
  description: z.string().nullable(),
  start_date: z.iso.datetime({ offset: true }), // ISO 8601 datetime string
  end_date: z.iso.datetime({ offset: true }),
  location: z.string().nullable(),
  url: z.string().nullable(),
  is_taught: z.boolean().nullable(),
  service_type_id: z.number(),
});

// Session schema with nested events
const SessionWithEventsSchema = z.object({
  session_id: z.number(),
  session_title: z.string(),
  session_description: z.string().nullable(),
  staff_id: z.uuid().nullable(),
  staff_first_name: z.string().nullable(),
  staff_last_name: z.string().nullable(),
  curriculum_notes: z.string().nullable(),
  resources: z.string().nullable(),
  events: z.array(EventSchema),
});

// Main class date ranges schema
export const ClassSummarySchema = z.object({
  class_id: z.number(),
  title: z.string(),
  description: z.string(),
  tags: z.array(z.string()),
  open_registration: z.boolean(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
  first_session_date: z.iso.datetime({ offset: true }).nullable(),
  last_session_date: z.iso.datetime({ offset: true }).nullable(),
  total_sessions: z.number(),
  total_events: z.number(),
  sessions_with_events: z.array(SessionWithEventsSchema),
});

// Infer TypeScript types from the schemas
export type Event = z.infer<typeof EventSchema>;
export type SessionWithEvents = z.infer<typeof SessionWithEventsSchema>;
export type ClassSummary = z.infer<typeof ClassSummarySchema>;

// SWR global configuration
const swrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 60000, // 1 minute
  focusThrottleInterval: 30000, // 30 seconds
  errorRetryCount: 3,
  shouldRetryOnError: true,
};

import { z } from "zod";
import { PUBLIC_DATA_CACHE_KEYS } from "@/services/entities/cache-keys";

/**
 * Gets all subjects
 * @returns Promise<Subject[]>
 */
const fetchSubjects = async (): Promise<SubjectSupa[]> => {
  const { data, error } = await supabaseClient.from("subjects").select("*").order("name");

  if (error) {
    console.error(`Error getting all subjects: ${JSON.stringify(error)}`);
    throw new Error("Error getting all subjects.");
  }

  return data ?? [];
};

/**
 * Returns all active and searchable tutors
 * Notes: Uses modified graphql query to remove sensitive data
 * @returns Promise<Tutor[]>
 */
const fetchTutors = async () => {
  const { data, error } = await supabaseClient
    .from("staff_public")
    .select("*, staff_subjects(subjects(name))");

  if (error) {
    console.error(`Error getting public tutors: ${JSON.stringify(error)}`);
    throw new Error("Error getting public tutors");
  }

  return data ?? [];
};

/**
 * Gets a tutor's information.
 * Note: Special graphql to remove sensitive data
 * @param id Id of a tutor
 * @returns Promise<Tutor>
 */
const fetchTutor = async (id: string) => {
  const { data, error } = await supabaseClient
    .from("staff_public")
    .select()
    .eq("staff_id", id)
    .maybeSingle();
  if (error) {
    console.error(`Error getting public tutor: ${JSON.stringify(error)}`);
    throw new Error("Error getting public tutor");
  }
  return data;
};

async function listClasses(): Promise<ClassSummary[]> {
  const { data, error } = await supabaseClient
    .from("class_summary")
    .select()
    .eq("open_registration", true);

  if (error) {
    console.error(`Error getting public classes: ${JSON.stringify(error)}`);
    throw new Error("Error getting class information");
  }

  const validatedClasses: ClassSummary[] = [];
  let validationErrorCount = 0;

  data.forEach((cls, index) => {
    const parsedResult = ClassSummarySchema.safeParse(cls);

    if (parsedResult.success) {
      validatedClasses.push(parsedResult.data);
    } else {
      validationErrorCount++;
      console.warn(`⚠️ Zod Validation Failed for class at index ${index}.`);
      // Log the specific Zod issues, which includes the path to the error
      console.error(JSON.stringify(parsedResult.error.issues, null, 2));
    }
  });

  if (validationErrorCount > 0) {
    console.warn(
      `Filter applied: Successfully validated ${validatedClasses.length} records. Ignored ${validationErrorCount} invalid records.`,
    );
  }

  return validatedClasses;
}

/**
 * Hook for accessing public subjects data
 * @returns Subject data and loading/error states
 */
export function useSubjects() {
  const {
    data = [],
    error,
    isLoading,
    mutate,
  } = useSWR<SubjectSupa[]>(PUBLIC_DATA_CACHE_KEYS.SUBJECTS, fetchSubjects, swrConfig);

  return {
    subjects: data,
    isLoading,
    error,
    refreshSubjects: mutate,
  };
}

/**
 * Hook for accessing public tutors list
 * @returns Tutor data and loading/error states
 */
export function useTutors() {
  const {
    data = [],
    error,
    isLoading,
    mutate,
  } = useSWR(PUBLIC_DATA_CACHE_KEYS.TUTORS, fetchTutors, swrConfig);

  return {
    tutors: data,
    isLoading,
    error,
    refreshTutors: mutate,
  };
}

/**
 * Hook for accessing a single public tutor's information
 * @param id The tutor's ID
 * @param initialData Optional initial data for the tutor
 * @returns Tutor data and loading/error states
 */
export function useTutor(id: string) {
  const { data, error, isLoading, mutate } = useSWR(
    id ? PUBLIC_DATA_CACHE_KEYS.TUTOR(id) : null,
    () => fetchTutor(id),
    {
      ...swrConfig,
    },
  );

  return {
    tutor: data,
    isLoading,
    error,
    refreshTutor: mutate,
  };
}

export async function createInquiry(input: InquirySupaInsert) {
  const { data, error } = await supabaseClient.from("inquiries").insert(input);
  if (error) {
    console.error("Error creating inquiry", error);
    throw error;
  }
  return data;
}

export function useClasses() {
  const {
    data = [],
    error,
    isLoading,
  } = useSWR<ClassSummary[]>(PUBLIC_DATA_CACHE_KEYS.CLASSES, listClasses, { ...swrConfig });

  return {
    classes: data,
    isLoading,
    error,
  };
}
